import { Link } from '@tanstack/react-router';
import { Pencil, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { ViewOnStorefrontButton } from '@/features/stores';
import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { usePolicies } from '../hooks/use-policies';
import { policyRows, type PolicyRow } from '../schemas/policy-rows';
import { POLICY_LIMIT, policyPath } from '../schemas/policy-rules';

import { PolicyStatusBadge } from './policy-status-badge';

function DocumentRow({
  storeId,
  row,
  editable,
}: {
  storeId: string;
  row: PolicyRow;
  editable: boolean;
}) {
  const { t } = useTranslation('policies');
  const format = useFormat();
  const { policy } = row;
  const label = policy ? policy.title : t(`kind.${row.kind}`);
  return (
    <TableRow aria-label={label}>
      <TableCell className="pl-6 align-top">
        <div className="flex min-w-0 flex-col">
          <span className={policy ? 'font-medium' : 'text-muted-foreground'}>{label}</span>
          <span className="text-muted-foreground truncate font-mono text-xs">
            {policy ? policyPath(policy.handle) : t(`kind.${row.kind}`)}
          </span>
        </div>
      </TableCell>
      <TableCell className="align-top">
        {policy ? (
          <PolicyStatusBadge status={policy.status} />
        ) : (
          <span className="text-muted-foreground text-sm">{t('documents.notCreated')}</span>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground align-top text-sm">
        {policy ? (
          <span title={format.utc(policy.updatedAt)}>{format.relative(policy.updatedAt)}</span>
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell className="pr-6 text-right align-top">
        {policy ? (
          <div className="flex justify-end gap-2">
            <ViewOnStorefrontButton
              url={policy.storefrontUrl}
              live={policy.status === 'published'}
              variant="ghost"
            />
            <Button asChild variant="outline" size="sm">
              <Link
                to="/s/$storeId/settings/policies/$policyId"
                params={{ storeId, policyId: policy.id }}
              >
                <Pencil aria-hidden />
                {t('documents.edit', { kind: label })}
              </Link>
            </Button>
          </div>
        ) : editable ? (
          <Button asChild variant="outline" size="sm">
            <Link
              to="/s/$storeId/settings/policies/new"
              params={{ storeId }}
              search={{ kind: row.kind }}
            >
              <Plus aria-hidden />
              {t('documents.create', { kind: label })}
            </Link>
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

/** One row per policy kind; the storefront serves each at `/policies/{handle}`. */
export function PolicyDocumentsCard({ storeId }: { storeId: string }) {
  const { t } = useTranslation('policies');
  const query = usePolicies(storeId);
  const rows = query.data ? policyRows(query.data) : null;
  const full = (query.data?.length ?? 0) >= POLICY_LIMIT;
  const { editable } = useStoreEditable({ permission: 'content.write' });

  return (
    <FormSection
      id={SECTION_ANCHORS.policyDocuments}
      tour="settings.policies.documents"
      title={t('documents.title')}
      description={t('documents.description')}
      className="gap-0 pb-0"
    >
      <div className="-mx-6 border-t">
        {query.error ? (
          <div className="p-6">
            <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />
          </div>
        ) : !rows ? (
          <div className="flex flex-col gap-2 p-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : (
          <Table aria-label={t('documents.title')}>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-6">{t('documents.columns.document')}</TableHead>
                <TableHead>{t('documents.columns.status')}</TableHead>
                <TableHead>{t('documents.columns.updated')}</TableHead>
                <TableHead className="pr-6 text-right" data-tour="settings.policies.row-action">
                  {t('documents.columns.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <DocumentRow
                  key={row.policy?.id ?? row.kind}
                  storeId={storeId}
                  row={row}
                  editable={editable}
                />
              ))}
            </TableBody>
          </Table>
        )}
        {editable ? (
          <div className="flex items-center justify-between gap-3 border-t px-6 py-3">
            <p className="text-muted-foreground text-xs">{t('documents.customHint')}</p>
            <Button
              asChild
              variant="ghost"
              size="sm"
              disabled={full}
              data-tour="settings.policies.add-custom"
            >
              <Link
                to="/s/$storeId/settings/policies/new"
                params={{ storeId }}
                search={{ kind: 'custom' }}
                aria-disabled={full || undefined}
              >
                <Plus aria-hidden />
                {t('documents.addCustom')}
              </Link>
            </Button>
          </div>
        ) : null}
      </div>
    </FormSection>
  );
}
